import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  DevHubEvent,
  DevHubEventInput,
  DevHubEventsPermissions,
  UpcomingEventsResponse,
} from './types';

/** Reads and manages DevHub events through the `devhub-events` backend. */
export interface DevHubEventsApi {
  getUpcoming(options?: { limit?: number }): Promise<UpcomingEventsResponse>;
  /** A single event (posted, static or Outlook) for the details view; rejects with a 404 error when not found. */
  getEvent(id: string): Promise<DevHubEvent>;
  /** What the signed-in user may do. Admins (Platform Team) can post, edit and delete events. */
  getPermissions(): Promise<DevHubEventsPermissions>;
  createEvent(input: DevHubEventInput): Promise<DevHubEvent>;
  updateEvent(id: string, input: DevHubEventInput): Promise<DevHubEvent>;
  deleteEvent(id: string): Promise<void>;
}

export const devhubEventsApiRef = createApiRef<DevHubEventsApi>({
  id: 'plugin.devhub-events.service',
});

export class DevHubEventsClient implements DevHubEventsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getUpcoming(options: { limit?: number } = {}): Promise<UpcomingEventsResponse> {
    const query =
      options.limit !== undefined ? `?limit=${encodeURIComponent(String(options.limit))}` : '';
    return this.request<UpcomingEventsResponse>(`/upcoming${query}`);
  }

  async getPermissions(): Promise<DevHubEventsPermissions> {
    return this.request<DevHubEventsPermissions>('/permissions');
  }

  async getEvent(id: string): Promise<DevHubEvent> {
    return this.request<DevHubEvent>(`/events/${encodeURIComponent(id)}`);
  }

  async createEvent(input: DevHubEventInput): Promise<DevHubEvent> {
    return this.request<DevHubEvent>('/events', { method: 'POST', body: input });
  }

  async updateEvent(id: string, input: DevHubEventInput): Promise<DevHubEvent> {
    return this.request<DevHubEvent>(`/events/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: input,
    });
  }

  async deleteEvent(id: string): Promise<void> {
    await this.request<void>(`/events/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  private async request<T>(
    path: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('devhub-events');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: options.method ?? 'GET',
      ...(options.body !== undefined && {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options.body),
      }),
    });
    if (!response.ok) {
      const error = await ResponseError.fromResponse(response);
      // Surface the backend's reason (e.g. "end must be after start") to the form.
      const reason = error.body?.error?.message;
      throw reason && response.status < 500 ? new Error(reason) : error;
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }
}
